import { RefObject, useEffect, useRef } from "react";
import { trapKeydown } from "./focusable";

// While `open`: focuses the dialog, traps Tab inside it, closes on Escape, and
// returns focus to the opener on close or unmount.
export const useFocusTrap = (
  ref: RefObject<HTMLElement | null>,
  open: boolean,
  onClose: () => void,
) => {
  // Latest onClose, so the listener bound once per open never calls a stale one.
  const onCloseRef = useRef(onClose);
  // Updated in an effect (no deps) so the ref only changes on commit, not mid-render.
  useEffect(() => {
    onCloseRef.current = onClose;
  });

  useEffect(() => {
    if (!open) return;
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    ref.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (!ref.current) return;
      trapKeydown(e, ref.current, document.activeElement, () => onCloseRef.current());
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      if (opener?.isConnected) opener.focus();
    };
  }, [open, ref]);
};
